import { Fragment } from 'react';

import { ActionIcon, Button, Divider, Flex, Group } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { randomId } from '@mantine/hooks';

import {
	BOOKING_PASS_TYPES,
	BOOKING_PASS_TYPES_KEYS,
	BOOKING_VISIT_REASONS,
} from '@/constant/booking';

import GuestPass from '@/entities/GuestFields/GuestPass.tsx';
import { RenterSelector } from '@/entities/RenterSelector';

import { TMeetingRoomForm } from '@/features/NewBooking/useMeetingRoomForm.tsx';

import useAppStore from '@/shared/store/app.store.ts';
import { IGuest } from '@/shared/types/booking.types.ts';
import { EmailInput, LabelInput, PhoneInput, Select } from '@/shared/uikit/components/input';
import { CarInput } from '@/shared/uikit/components/input/CarInput/CarInput.tsx';

import { IconBuilding, IconList, IconTrash, IconUserFilled } from '@tabler/icons-react';

type TGuestFieldsProps = {
	isParking?: boolean;
	isShowCarNumberField?: boolean;
	isExternal?: boolean;
	form: UseFormReturnType<{ [key: string]: unknown; guests: IGuest[] }>;
};

interface IGuestWithCarNumber extends IGuest {
	isCarNumber?: boolean;
}

export const GuestFields = ({
	form,
	isParking = false,
	isShowCarNumberField = true,
	isExternal = false,
}: TGuestFieldsProps) => {
	const isMobile = useAppStore((state) => state.isMobile);

	return (
		<Fragment>
			{form.values.guests?.map((item, index) => {
				const passTypeProps = form.getInputProps(`guests.${index}.pass_type`);

				const isVisibleCarNumberField =
					isShowCarNumberField &&
					(isExternal
						? passTypeProps.value === BOOKING_PASS_TYPES_KEYS.ENTRANCE_PARKING ||
							passTypeProps.value === BOOKING_PASS_TYPES_KEYS.PARKING
						: true);

				return (
					<Fragment key={item?.key ?? item?.id}>
						{isParking ? (
							<Group>
								<LabelInput
									label="ФИО"
									placeholder="Укажите ФИО гостя"
									rightSection={<IconUserFilled opacity={0.5} />}
									{...form.getInputProps(`guests.${index}.full_name`)}
								/>
								<RenterSelector
									value={form.values.guests?.[index]?.renter_id ?? null}
									onChange={(renterId) => form.setFieldValue(`guests.${index}.renter_id`, renterId)}
								/>
								<EmailInput {...form.getInputProps(`guests.${index}.email`)} />
								<PhoneInput
									value={form.values.guests?.[index]?.phone ?? ''}
									onChange={(phone) => form.setFieldValue(`guests.${index}.phone`, phone)}
								/>
							</Group>
						) : (
							<>
								<Divider />
								<Flex align="center" gap="md">
									<Group w="100%">
										<Group w="100%" grow={!isMobile}>
											<LabelInput
												label="ФИО"
												placeholder="Укажите ФИО гостя"
												rightSection={<IconUserFilled opacity={0.5} />}
												{...form.getInputProps(`guests.${index}.full_name`)}
												required
											/>
											<EmailInput {...form.getInputProps(`guests.${index}.email`)} required />
										</Group>

										<Group w="100%" grow={!isMobile}>
											<PhoneInput
												value={form.values.guests?.[index]?.phone ?? ''}
												onChange={(phone) => form.setFieldValue(`guests.${index}.phone`, phone)}
											/>
											<LabelInput
												label="Компания"
												placeholder="Укажите компанию гостя"
												rightSection={<IconBuilding opacity={0.5} />}
												{...form.getInputProps(`guests.${index}.company`)}
											/>
											{isVisibleCarNumberField && (
												<CarInput
													required={
														passTypeProps.value === BOOKING_PASS_TYPES_KEYS.ENTRANCE_PARKING ||
														passTypeProps.value === BOOKING_PASS_TYPES_KEYS.PARKING
													}
													{...form.getInputProps(`guests.${index}.car_number`)}
												/>
											)}
										</Group>

										{isExternal && (
											<>
												<Group w="100%" grow={!isMobile}>
													<Select
														caption="Цель посещения"
														placeholder="Выберите из списка"
														rightSection={<IconList opacity={0.5} />}
														data={BOOKING_VISIT_REASONS}
														{...form.getInputProps(`guests.${index}.visit_reason`)}
													/>
												</Group>

												<Group w="100%" grow={!isMobile}>
													<Select
														caption="Вид пропуска"
														placeholder="Выберите из списка"
														rightSection={<IconList opacity={0.5} />}
														data={
															isShowCarNumberField
																? BOOKING_PASS_TYPES
																: BOOKING_PASS_TYPES.filter(
																		(elem) => elem.value === BOOKING_PASS_TYPES_KEYS.ENTRANCE,
																	)
														}
														{...passTypeProps}
														onChange={(value) => {
															passTypeProps.onChange(value);

															if (!value || value === BOOKING_PASS_TYPES_KEYS.ENTRANCE) {
																form.getInputProps(`guests.${index}.car_number`).onChange(null);
															}
														}}
													/>
												</Group>
											</>
										)}
									</Group>
									<ActionIcon color="red" onClick={() => form.removeListItem('guests', index)}>
										<IconTrash size="1.2rem" />
									</ActionIcon>
								</Flex>
							</>
						)}
					</Fragment>
				);
			})}
			{!isParking && (
				<Button
					variant="default"
					size="md"
					radius="lg"
					onClick={() =>
						form.insertListItem('guests', {
							full_name: '',
							email: '',
							phone: '',
							company: '',
							car_number: null,
							isNeedPass: false,
							visit_reason: null,
							pass_type: null,
							key: randomId(),
						})
					}
				>
					Добавить гостя
				</Button>
			)}
		</Fragment>
	);
};
